import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  WorkFlowConfiguration,
  WorkflowPermissionDetailsAssigneeTabList,
  WorkFlowStep,
} from './workflow.model';
import { ApiResponse, Meta } from '../../../common/models/api-response.model';
import { RequestDetailsResponse } from '../../../common/models/request-details.model';
import { baseUrl } from '../../../common/constants/endpoints.constants';
import { SearchEnum } from '../../../common/enums/search.enum';

@Injectable({
  providedIn: 'root',
})
export class WorkflowService {
  http = inject(HttpClient);
  baseUrlPrefix = `${baseUrl}`;

  getWorkflowOwnerDropdown(
    flowTemplateCategoryId: string | number,
    name: string,
    page: number
  ) {
    const params = new HttpParams()
      .append('filters[search]', name)
      .append('page', page)
      .append('sort[added_in_permission][type]', 'desc')
      .append('sort[added_in_permission][data][permission]', 'own')
      .append(
        'sort[added_in_permission][data][resource_type]',
        'flow_template_category'
      )
      .append(
        'sort[added_in_permission][data][resource_id]',
        flowTemplateCategoryId
      );
    return this.http.get<{ data: any[]; meta: Meta }>(
      `${this.baseUrlPrefix}/dashboard/flow-template-categories/${flowTemplateCategoryId}/owners`,
      { params }
    );
  }

  getWorkflowCategoryPermissionList(page: number, perPage: number) {
    const params = new HttpParams()
      .append('page', page)
      .append('per_page', perPage);
    return this.http.get(
      `${this.baseUrlPrefix}/dashboard/flow-template-categories`,
      { params }
    );
  }

  getWorkFlowCategoryDetails(id: string) {
    return this.http.get(
      `${this.baseUrlPrefix}/dashboard/flow-template-categories/${id}`
    );
  }

  getWorkFlowCategoryOwners(id: string, page: number, perPage: number) {
    const params = new HttpParams()
      .append('page', page)
      .append('per_page', perPage)
      .append(`${'sort[name][type]'}`, 'asc');
    return this.http.get(
      `${this.baseUrlPrefix}/dashboard/flow-template-categories/${id}/owners`,
      { params }
    );
  }

  updateFlowTemplateCategory(flowTemplateCategoryId: string, data: any) {
    return this.http.put(
      `${this.baseUrlPrefix}/dashboard/flow-template-categories/${flowTemplateCategoryId}`,
      data
    );
  }

  getWorkflowConfigurationList(
    page: number,
    perPage: number,
    search?: string
  ) {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', perPage);
    if (search) {
      params = params.append(SearchEnum.keyword, search.trim());
    }
    return this.http.get<ApiResponse<WorkFlowConfiguration>>(
      `${this.baseUrlPrefix}/dashboard/configures/flow-templates`,
      { params }
    );
  }

  getWorkFlowShow(flowTemplateId: string | number) {
    return this.http.get<RequestDetailsResponse>(
      `${this.baseUrlPrefix}/dashboard/flow-templates/${flowTemplateId}`
    );
  }

  getWorkFlowStepsList(flowTemplateId: string | number) {
    return this.http.get<ApiResponse<WorkFlowStep>>(
      `${this.baseUrlPrefix}/dashboard/flow-templates/${flowTemplateId}/step-templates?filters[is_configurable]=1&per_page=25`
    );
  }

  /** The steps the configuration page cannot edit, listed there only for their step hooks. */
  getNonConfigurableWorkFlowSteps(flowTemplateId: string | number) {
    const params = new HttpParams()
      .append('filters[is_configurable]', 0)
      .append('per_page', 100);
    return this.http.get<ApiResponse<WorkFlowStep>>(
      `${this.baseUrlPrefix}/dashboard/flow-templates/${flowTemplateId}/step-templates`,
      { params }
    );
  }

  updateWorkFlowStep(
    flowTemplateId: string | number,
    stepId: string | number,
    data: any
  ) {
    return this.http.put(
      `${this.baseUrlPrefix}/dashboard/flow-templates/${flowTemplateId}/step-templates/${stepId}`,
      data
    );
  }

  getWorkflowPermissionList(
    page: number,
    perPage: number,
    sort?: { key: string; value: 'asc' | 'desc' },
    search?: string
  ) {
    let params = new HttpParams()
      .append('page', page)
      .append('per_page', perPage);

    if (sort?.key) {
      params = params.append(`sort[${sort.key}]`, sort.value);
    }
    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }

    return this.http.get(
      `${this.baseUrlPrefix}/dashboard/permissions/flow-templates`,
      { params }
    );
  }

  getWorkflowPermissionDetails(flowTemplateId: string) {
    return this.http.get(
      `${this.baseUrlPrefix}/dashboard/flow-templates/${flowTemplateId}`
    );
  }

  getWorkflowPermissionUsersTable(
    flowTemplateId: string | number,
    page: number,
    perPage: number
  ) {
    const params = new HttpParams()
      .append('page', page)
      .append('per_page', perPage);
    return this.http.get(
      `${this.baseUrlPrefix}/dashboard/flow-templates/${flowTemplateId}/users`,
      { params }
    );
  }

  addWorkflowMember(flowTemplateId: string | number, data: any) {
    return this.http.post(
      `${this.baseUrlPrefix}/dashboard/flow-templates/${flowTemplateId}/members/bulk`,
      data
    );
  }

  updateWorkflowMember(
    flowTemplateId: string | number,
    memberId: string | number,
    data: any
  ) {
    return this.http.put(
      `${this.baseUrlPrefix}/dashboard/flow-templates/${flowTemplateId}/members/${memberId}`,
      data
    );
  }

  removeWorkflowMember(flowTemplateId: string, memberId: string) {
    return this.http.delete(
      `${this.baseUrlPrefix}/dashboard/flow-templates/${flowTemplateId}/members/${memberId}`
    );
  }

  getWorkflowMemberDetails(flowTemplateId: string, memberId: string) {
    return this.http.get(
      `${this.baseUrlPrefix}/dashboard/flow-templates/${flowTemplateId}/members/${memberId}`
    );
  }

  getWorkflowAssigneeList(
    flowTemplateId: string,
    page: number,
    perPage: number
  ) {
    const params = new HttpParams()
      .append('page', page)
      .append('per_page', perPage);
    return this.http.get<{
      data: WorkflowPermissionDetailsAssigneeTabList[];
      meta: Meta;
    }>(
      `${this.baseUrlPrefix}/dashboard/flow-templates/${flowTemplateId}/assignees`,
      { params }
    );
  }
}
